// The Host switch card of the game's General tab (M7 slice 3; spec/dashboard.md section 12.4; spec/api-conventions.md
// section 21.3): the published version, when and by whom, the hosts (or nothing published), the public key a build bakes
// with Copy; Edit… (`host-switch-dialog`, the five hosts and a note, the game slug typed to confirm because it re-points
// every build on its next start) → `PUT …/host-switch` with If-Match of the row version; Withdraw… (`DELETE`, confirm);
// Sign without publishing… (`POST …/host-switch:sign`: the signed document for the offline path, Copy / Download). The
// write actions need deploy on production (the API answers 403 anyway); the audit rows of `host_switch` are beneath.
import { Fragment, useEffect, useRef, useState, type FormEvent } from 'react';
import { ops } from '../../api/operations';
import type { AuditEntry, HostSwitch, Page } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { downloadText } from '../../content/localization/strings';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { useConfirm } from '../../ui/ConfirmDialog';
import { Field } from '../../ui/Field';
import { TextArea, TextInput } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { formatInstantWithZone, useTimeZone } from '../../ui/time';
import { useToasts } from '../../ui/Toast';
import { queryCache, useQuery } from '../../workspace/cache';
import { CopyText } from '../org/Integrations';
import { AuditPanel } from './AuditPanel';
import { asApiError, AUDIT_PAGE_SIZE, type AuditFilterKey } from './keys';
import '../org/org.css';
import { LoadError, SkeletonRows } from '../../ui/PageStates';
import { AlertTriangle, Pencil } from '../../ui/icons';

export const HOST_KEYS = ['content', 'assets', 'addressables', 'api', 'switch'] as const;
type HostKey = (typeof HOST_KEYS)[number];
const HOST_LABEL: Record<HostKey, string> = { content: 'Content host', assets: 'Assets host', addressables: 'Addressables host', api: 'API host', switch: 'Switch URL' };
export const NOTHING_PUBLISHED = 'Nothing published — every build runs on its baked hosts.';
const AUDIT_KEYS: readonly AuditFilterKey[] = ['action', 'user', 'from', 'to'];

function hostsOf(s: HostSwitch | undefined): Partial<Record<HostKey, string>> {
  const h = s?.hosts;
  if (!h || typeof h !== 'object' || Array.isArray(h)) return {};
  const out: Partial<Record<HostKey, string>> = {};
  for (const k of HOST_KEYS) {
    const v = (h as Record<string, unknown>)[k];
    if (typeof v === 'string' && v) out[k] = v;
  }
  return out;
}

export function HostSwitchCard() {
  const { org, game, can, offsets } = useGame();
  const { client } = useAuth();
  const toasts = useToasts();
  const confirm = useConfirm();
  const [zone] = useTimeZone();
  const when = (v: string | null | undefined) => formatInstantWithZone(v, zone, { offsets });
  const key = `game:${org}/${game.slug}:host-switch`;
  const path = { org, game: game.slug };
  const q = useQuery(key, () => client.data(ops.getHostSwitch, { path }));
  const [editing, setEditing] = useState(false);
  const [signing, setSigning] = useState(false);
  const [busy, setBusy] = useState(false);
  const canDeploy = can('deploy', 'prod');
  const refresh = () => {
    queryCache.invalidate(key);
    queryCache.invalidate(`game:${org}/${game.slug}:host-switch-audit`);
  };
  const s = q.data;
  const hosts = hostsOf(s);
  const published = Boolean(s?.publishedAt) && Object.keys(hosts).length > 0;

  const withdraw = async () => {
    if (!s) return;
    const ok = await confirm({ title: 'Withdraw the host switch?', body: 'The published document is deleted: every build goes back to its baked hosts on its next start.', confirmLabel: 'Withdraw', tone: 'danger', typeToConfirm: game.slug });
    if (!ok) return;
    setBusy(true);
    try {
      await client.call(ops.deleteHostSwitch, { path, ifMatch: Number(s.rowVersion) });
      toasts.success('The host switch was withdrawn.');
    } catch (err) {
      const api = asApiError(err);
      toasts.error(api.isVersionConflict ? 'Someone changed the host switch meanwhile; it was re-read.' : api.message, { requestId: api.requestId });
    } finally {
      setBusy(false);
      refresh();
    }
  };

  return (
    <section className="pane settings-form" data-testid="host-switch" aria-label="Host switch">
      <div className="pane-head">
        <h2>Host switch</h2>
        {s && (published ? <span className="status-dot status-live">Live</span> : <span className="status-dot status-off">Baked hosts</span>)}
        <div className="spacer" />
        {canDeploy && s && (
          <>
            <Button size="s" onClick={() => setSigning(true)}>
              Sign without publishing…
            </Button>
            <Button size="s" icon={<Pencil aria-hidden="true" />} onClick={() => setEditing(true)}>
              Edit…
            </Button>
            {published && (
              <Button size="s" variant="danger" onClick={() => void withdraw()} busy={busy}>
                Withdraw…
              </Button>
            )}
          </>
        )}
      </div>
      <div className="pane-body host-switch-body">
      <p className="muted small">A signed document the SDK reads on start: it re-points the builds' content, asset, Addressables and API hosts without a new build.</p>
      {q.error ? (
        <LoadError>The host switch could not be read: {asApiError(q.error).message}</LoadError>
      ) : !s ? (
        <SkeletonRows rows={2} />
      ) : (
        <>
          <dl className="kv">
            <dt>Version</dt>
            <dd data-testid="host-switch-version">{String(s.version)}</dd>
            <dt>Published</dt>
            <dd>{s.publishedAt ? when(s.publishedAt) : 'never'}</dd>
            {s.updatedBy && (
              <>
                <dt>By</dt>
                <dd>
                  {s.updatedBy.name ?? s.updatedBy.id} <span className="muted small">({s.updatedBy.type})</span>
                </dd>
              </>
            )}
            {s.note && (
              <>
                <dt>Note</dt>
                <dd>{s.note}</dd>
              </>
            )}
          </dl>
          {published ? (
            <dl className="kv" data-testid="host-switch-hosts">
              {HOST_KEYS.filter((k) => hosts[k]).map((k) => (
                <Fragment key={k}>
                  <dt>{HOST_LABEL[k]}</dt>
                  <dd>
                    <code>{hosts[k]}</code>
                  </dd>
                </Fragment>
              ))}
            </dl>
          ) : (
            <p className="muted" data-testid="host-switch-hosts">
              {NOTHING_PUBLISHED}
            </p>
          )}
          <dl className="kv">
            <dt>Document</dt>
            <dd>
              <code>{s.url}</code>
            </dd>
          </dl>
          <div className="settings-key-block">
            <div className="field-label">Public key</div>
            <div className="secret-row secret-top">
              <pre data-testid="host-switch-public-key" className="settings-public-key">{s.publicKeyPem}</pre>
              <CopyText value={s.publicKeyPem} label="Switch public key" />
            </div>
          </div>
          <p className="muted small">A build verifies the document with this key (the SDK's SwitchPublicKeyPem); bake it into the builds that should follow the switch.</p>
          {!canDeploy && (
            <p className="muted small" role="note">
              Publishing, withdrawing and signing need deploy on production.
            </p>
          )}
        </>
      )}
      </div>
      {can('read_audit') && (
        <div data-testid="host-switch-audit" className="pane-body host-switch-audit">
          <h3>Changes</h3>
          <AuditPanel
            cacheKey={`game:${org}/${game.slug}:host-switch-audit`}
            filterKeys={AUDIT_KEYS}
            fetchPage={(filters, cursor) => client.data(ops.listGameAudit, { path, query: { ...filters, resourceType: 'host_switch', limit: AUDIT_PAGE_SIZE, ...(cursor ? { cursor } : {}) } }) as unknown as Promise<Page<AuditEntry>>}
            when={when}
            ctx={{ offsets }}
          />
        </div>
      )}
      {s && (
        <HostsDialog
          mode="publish"
          open={editing}
          current={s}
          slug={game.slug}
          onClose={() => setEditing(false)}
          onDone={() => {
            setEditing(false);
            refresh();
          }}
        />
      )}
      {s && <HostsDialog mode="sign" open={signing} current={s} slug={game.slug} onClose={() => setSigning(false)} onDone={() => refresh()} />}
    </section>
  );
}

function HostsDialog({ mode, open, current, slug, onClose, onDone }: { mode: 'publish' | 'sign'; open: boolean; current: HostSwitch; slug: string; onClose: () => void; onDone: () => void }) {
  const { org, game } = useGame();
  const { client } = useAuth();
  const toasts = useToasts();
  const [hosts, setHosts] = useState<Partial<Record<HostKey, string>>>({});
  const [note, setNote] = useState('');
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<ReturnType<typeof asApiError> | null>(null);
  const [busy, setBusy] = useState(false);
  const [signed, setSigned] = useState<{ text: string; version: string } | null>(null);
  // the form starts from the card's values when it opens; a re-read of the card while open (after a sign) leaves it alone
  const currentRef = useRef(current);
  currentRef.current = current;
  useEffect(() => {
    if (open) {
      setHosts(hostsOf(currentRef.current));
      setNote(currentRef.current.note ?? '');
      setTyped('');
      setError(null);
      setSigned(null);
    }
  }, [open]);

  const body = () => {
    const out: Record<string, string> = {};
    for (const k of HOST_KEYS) {
      const v = hosts[k]?.trim();
      if (v) out[k] = v;
    }
    return { hosts: out, note: note.trim() || null };
  };
  const ready = mode === 'sign' || typed === slug;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === 'publish') {
        const r = await client.data(ops.publishHostSwitch, { path: { org, game: game.slug }, body: body(), ifMatch: Number(current.rowVersion) });
        toasts.success(`Host switch version ${String(r.version)} published.`);
        onDone();
      } else {
        const r = await client.data(ops.signHostSwitch, { path: { org, game: game.slug }, body: body() });
        setSigned({ text: JSON.stringify(r.document, null, 2), version: String(r.version) });
        onDone();
      }
    } catch (err) {
      const api = asApiError(err);
      if (api.isVersionConflict) {
        toasts.error('Someone changed the host switch meanwhile; it was re-read. Review and publish again.', { requestId: api.requestId });
        onDone();
      } else setError(api);
    } finally {
      setBusy(false);
    }
  };

  const title = mode === 'publish' ? 'Publish the host switch' : 'Sign a host switch without publishing';
  return (
    <Modal title={title} open={open} onClose={onClose} size="m">
      {signed ? (
        <div data-testid="host-switch-signed">
          <p className="small">
            Signed as version {signed.version}; nothing was published. Keep it with the operator's secrets: uploaded to <code>{current.url.replace(/^\/content\//, '')}</code> in the releases bucket, it re-points every build on its next start even while the API is down.
          </p>
          <pre className="signed-document" data-testid="host-switch-document">
            {signed.text}
          </pre>
          <div className="modal-footer form-footer">
            <CopyText value={signed.text} label="Signed document" />
            <Button
              onClick={() => {
                downloadText('switch.json', signed.text, 'application/json');
                toasts.success('Downloaded switch.json');
              }}
            >
              Download
            </Button>
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={(e) => void submit(e)} data-testid={mode === 'publish' ? 'host-switch-dialog' : 'host-switch-sign-dialog'}>
          <p className="small">Absolute http(s) URLs; an empty field keeps the host the build baked.{mode === 'publish' ? ' Leaving every field empty withdraws every override.' : ''}</p>
          {HOST_KEYS.map((k) => (
            <Field key={k} label={HOST_LABEL[k]} error={error?.fieldMessage(`hosts.${k}`)}>
              <TextInput aria-label={HOST_LABEL[k]} value={hosts[k] ?? ''} onChange={(e) => setHosts((cur) => ({ ...cur, [k]: e.target.value }))} autoComplete="off" placeholder="https://" />
            </Field>
          ))}
          <Field label="Note" error={error?.fieldMessage('note')}>
            <TextArea aria-label="Note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
          {mode === 'publish' && (
            <>
              <div className="banner banner-warning" role="note">
                <AlertTriangle className="banner-icon" aria-hidden="true" />
                <div className="banner-body">Publishing re-points every build on its next start.</div>
              </div>
              <Field label={`Type "${slug}" to confirm`} required>
                <TextInput aria-label={`Type "${slug}" to confirm`} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
              </Field>
            </>
          )}
          {error && !error.details?.length && (
            <LoadError>{error.message}</LoadError>
          )}
          <div className="modal-footer form-footer">
            <Button onClick={onClose}>Cancel</Button>
            <Button type="submit" variant={mode === 'publish' ? 'danger' : 'primary'} busy={busy} disabled={!ready}>
              {mode === 'publish' ? 'Publish' : 'Sign'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
